<script lang="ts" setup>
import { app } from '@/composition/app'
import { FormState } from '@/composition/forms'
import { GroupState } from '@/composition/groups'
import { UserState } from '@/composition/users'
import { MemberStatus, MembershipType, SubscriptionType } from '@/models/Groups'
import { useGlobalStore } from '@/store/app'
import { capitalizeWord } from '@/util/stringFormatting'
import {
  GAEventTypes,
  currency,
  trackCustomGAEvents,
} from '@ghl/clientportal-core'
import { useNotification } from '@gohighlevel/ghl-ui'
import { computed, defineAsyncComponent, onMounted, ref, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import { useRoute, useRouter } from 'vue-router'
import { UI_PAYMENT_ELEMENT_EVENT } from '../../helper/const'
import { ColorLuminance } from '../helper'
import ReviewMembershipAnswers from './groupMembers/ReviewMembershipAnswers.vue'

const LoginModal = defineAsyncComponent(() => import('../login/LoginModal.vue'))
const GroupInfoWeb = defineAsyncComponent(() => import('./GroupInfo.vue'))
const GroupInfoMobile = defineAsyncComponent(
  () => import('./GroupInfoMobile.vue')
)
const MembershipQuestions = defineAsyncComponent(
  () => import('./membershipQuestions/MembershipQuestions.vue')
)
const PaymentModal = defineAsyncComponent(
  () => import('./payments/PaymentModal.vue')
)
const PaymentSuccessfulModal = defineAsyncComponent(
  () => import('./payments/PaymentSuccessfulModal.vue')
)

const appStore = useGlobalStore()
const joinGroupLoader = ref(false)
const router = useRouter()
const route = useRoute()
const showMembershipQuestions = ref(false)
const showMembershipAnswers = ref(false)
const showPaymentModal = ref(false)
const showPaymentSuccessModal = ref(false)
const showLoginModal = ref(false)
const loginPage = ref('LOGIN')
const { t } = useI18n()

onMounted(() => {
  if (route.name === 'private-group' && route.query.joinGroup == 'true') {
    handleJoinGroup()
  }
})

// Watch for locationId to be available before checking invite
watch(
  () => [appStore.locationId, appStore.isAuthenticated] as const,
  ([locationId, isAuthenticated]) => {
    if (locationId && route.query?.invite && isAuthenticated === false) {
      loginPage.value = 'SIGN_UP'
      showLoginModal.value = true
    }
  },
  { immediate: true }
)

async function openMembers() {
  if (route.name === 'private-group' || route.name === 'inactive-group') {
    return
  }
  router.push({ name: 'communities-settings' })
}

const joinGroupText = computed(() => {
  const membershipType =
    GroupState.state.groupData?.settings?.membership?.membershipType
  if (
    membershipType === MembershipType.PAID &&
    (!GroupState.state?.userStatus ||
      GroupState.state?.userStatus == MemberStatus.PAYMENT_INITIATED)
  ) {
    const membership = GroupState.state.groupData?.settings?.membership
    if (
      membership?.trialDays > 0 &&
      membership.subscriptionType === SubscriptionType.RECURRING
    ) {
      return `${membership.trialDays} ${t('joinGroupModal.dayFreeTrial')}`
    }
    return membership.subscriptionType === SubscriptionType.RECURRING
      ? `${t('joinGroupModal.subscribeFor')} ${
          currency[membership.currency].symbol
        }${membership.price}/${capitalizeWord(membership.subscriptionInterval)}`
      : `${t('joinGroupModal.subscribeFor')} ${
          currency[membership.currency].symbol
        }${membership.price}`
  }

  if (GroupState.state.userStatus === MemberStatus.REQUESTED) {
    return t('joinGroupModal.membershipPending')
  }
  return t('joinGroupModal.joinGroup')
})

const lightColor = computed(() => {
  return `background-color: ${ColorLuminance(
    GroupState.state.groupData?.branding?.brandColor
      ? GroupState.state.groupData?.branding?.brandColor
      : 'black',
    0.2
  )}`
})

const notification = useNotification()
function successNotificationAlert(title: string, content: string) {
  notification.success({
    title: title,
    content: content,
    duration: 10000,
  })
}

function paymentCallback(event) {
  if (event.type === UI_PAYMENT_ELEMENT_EVENT.SUCCESS) {
    showPaymentSuccessModal.value = true
    showPaymentModal.value = false
    addUserToGroup()
  }
}

async function handleJoinGroup() {
  if (!appStore.isAuthenticated) {
    showLoginModal.value = true
    return
  }

  if (
    appStore.isAuthenticated &&
    (GroupState?.state?.userNotInitiatedJoinGroupProcess ||
      GroupState?.state?.userStatus === MemberStatus.PAYMENT_RECEIVED)
  ) {
    joinGroupLoader.value = true
    const membershipType =
      GroupState.state.groupData?.settings?.membership?.membershipType
    if (
      membershipType === MembershipType.PAID &&
      GroupState?.state?.userStatus !== MemberStatus.PAYMENT_RECEIVED
    ) {
      showPaymentModal.value = true
      joinGroupLoader.value = false
      return true
    }
    addUserToGroup()
  } else {
    showLoginModal.value = false
    if (
      GroupState.state.userMembershipAnswers &&
      GroupState.state.userStatus === MemberStatus.REQUESTED
    ) {
      showMembershipAnswers.value = true
      trackCustomGAEvents(GAEventTypes.MEMBER_VIEWED_OWN_ANSWERS, {
        locationId: GroupState.state.groupData.locationId,
      })
      return
    }
  }
}
async function addUserToGroup() {
  // dont send join group request if membership questions are enabled and not answered yet
  if (
    GroupState.state.groupData?.settings?.membershipQuestionsEnabled &&
    FormState.state?.membershipQuestions?.questions?.length > 0
  ) {
    showMembershipQuestions.value = true
    joinGroupLoader.value = false
    return
  }
  joinGroupLoader.value = true
  await GroupState.joinGroup()
  joinGroupLoader.value = false

  const membershipType =
    GroupState.state.groupData?.settings?.membership?.membershipType
  if (
    GroupState.state.userStatus === MemberStatus.REQUESTED &&
    membershipType !== MembershipType.PAID
  ) {
    successNotificationAlert(
      t('joinGroupModal.wahoo'),
      t('joinGroupModal.reviewRequestSoon')
    )
  }
}

function handleMembershipQuestionsModalClose(answersSubmitted: boolean) {
  showMembershipQuestions.value = false
  if (answersSubmitted) {
    successNotificationAlert(
      t('joinGroupModal.wahoo'),
      t('joinGroupModal.reviewRequestSoon')
    )
  }
}

function handleLoginAction() {
  handleJoinGroup()
}
</script>

<template>
  <div id="groupInfo" class="relative">
    <GroupInfoWeb
      v-if="!app.state.isMobile"
      :join-group-text="joinGroupText"
      :join-group-loader="joinGroupLoader"
      :light-color="lightColor"
      @openMembers="openMembers"
      @handleJoinGroup="handleJoinGroup"
    />
    <GroupInfoMobile
      v-else
      :join-group-text="joinGroupText"
      :join-group-loader="joinGroupLoader"
      :light-color="lightColor"
      @openMembers="openMembers"
      @handleJoinGroup="handleJoinGroup"
    />
    <!-- <div v-if="showStickyHeader && app.state.isMobile">
      <TopNavBarMobile></TopNavBarMobile>
    </div> -->
    <PaymentModal
      v-if="showPaymentModal"
      :show="showPaymentModal"
      :price-ids="
        GroupState.state.groupData.settings?.membership?.paymentPriceId
      "
      :is-live-payment="
        GroupState.state.groupData.settings?.membership?.isLivePayment
      "
      :membership="GroupState.state.groupData?.settings?.membership"
      :product-name="GroupState?.state?.groupData?.name"
      :source-id="'group-' + GroupState?.state?.groupData?._id"
      @update:confirmed="paymentCallback"
      @update:show="showPaymentModal = false"
    />
    <PaymentSuccessfulModal
      v-if="showPaymentSuccessModal"
      :show="showPaymentSuccessModal"
      :join-group-loader="joinGroupLoader"
      @update:show="showPaymentSuccessModal = false"
    />
    <LoginModal
      v-if="showLoginModal"
      :page="loginPage"
      :location-data="app.state.locationData"
      :show="showLoginModal"
      :login-confirmation="handleLoginAction"
      @update:show="showLoginModal = false"
    />
    <MembershipQuestions
      v-if="GroupState.state.groupData?.settings?.membershipQuestionsEnabled"
      :show="showMembershipQuestions"
      @update:show="handleMembershipQuestionsModalClose"
    />
    <ReviewMembershipAnswers
      :show="showMembershipAnswers"
      :membership-answers="GroupState.state.userMembershipAnswers"
      :contact-id="GroupState?.state?.userMembershipAnswers?.contactId"
      :full-name="UserState.state?.currentUser?.fullName || ''"
      :show-action-footer="false"
      :membership-request-callback="() => {}"
      @update:show="showMembershipAnswers = false"
    />
  </div>
</template>
<style scoped></style>
